import clsx from 'clsx';
import { useState } from 'react';
import { Label, Input, Checkbox } from '@/components/FormElements';
import generalConfig from '@/config/general';
import { getEditorAreaControlId } from '@/components/Editor/hooks/useEditingAreas';
import controlStyles from '@/components/FormElements/styles.module.scss';
import RevealedSection from '@/components/Editor/Form/fields/RevealedSection';
import parentStyles from '@/features/WidgetEdit/Form/styles.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

const EditorFormQuestions = _ref => {
  let {
    config,
    sectionId,
    onChange
  } = _ref;
  const {
    0: isValidShareElementId,
    1: setIsValidShareElementId
  } = useState(null);
  const {
    0: isValidLocalConfigId,
    1: setIsValidLocalConfigId
  } = useState(null);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(RevealedSection, {
      elementId: getEditorAreaControlId({
        sectionId,
        sectionEditingArea: 'button'
      }),
      label: "Button",
      children: /*#__PURE__*/_jsx(Label, {
        forInput: "form-cta",
        className: parentStyles.formField,
        text: "Submit Button (Text & Color)",
        children: /*#__PURE__*/_jsxs("div", {
          className: parentStyles.formField__row,
          children: [/*#__PURE__*/_jsx(Input, {
            id: "form-cta",
            placeholder: "Call to action",
            defaultValue: config.ctaButton,
            onChange: evt => onChange('', {
              ctaButton: evt.target.value
            })
          }), /*#__PURE__*/_jsx(Input, {
            type: "color",
            className: parentStyles.formField__row__colorPicker,
            defaultValue: config.ctaButtonColor || generalConfig.colors.bluePrimary,
            onChange: evt => onChange('', {
              ctaButtonColor: evt.target.value
            })
          })]
        })
      })
    }), /*#__PURE__*/_jsxs(RevealedSection, {
      label: "Save Settings",
      children: [/*#__PURE__*/_jsxs(Checkbox, {
        id: "form-session-storage",
        className: parentStyles.formField,
        checked: Boolean(config.saveSession),
        onChange: evt => onChange('', {
          saveSession: evt.target.checked
        }),
        children: [/*#__PURE__*/_jsx("span", {
          className: controlStyles.optionsInputHeading,
          children: "Save Session"
        }), /*#__PURE__*/_jsx("span", {
          className: clsx(controlStyles.optionsInputSubHeading, controlStyles['optionsInputSubHeading--expanded']),
          children: "Store user's answers to their current browser session."
        })]
      }), /*#__PURE__*/_jsxs(Checkbox, {
        id: "form-local-storage",
        className: parentStyles.formField,
        checked: Boolean(config.saveLocally),
        onChange: evt => onChange('', {
          saveLocally: evt.target.checked
        }),
        children: [/*#__PURE__*/_jsx("span", {
          className: controlStyles.optionsInputHeading,
          children: "Save Locally"
        }), /*#__PURE__*/_jsx("span", {
          className: clsx(controlStyles.optionsInputSubHeading, controlStyles['optionsInputSubHeading--expanded']),
          children: "Store user's answers to their device to persist them beyond a single browsing session"
        })]
      })]
    }), /*#__PURE__*/_jsxs(RevealedSection, {
      label: "Share Settings",
      children: [/*#__PURE__*/_jsxs(Checkbox, {
        id: "form-local-storage",
        className: parentStyles.formField,
        checked: Boolean(config.saveLocally),
        onChange: evt => onChange('', {
          shareable: evt.target.checked
        }),
        children: [/*#__PURE__*/_jsx("span", {
          className: controlStyles.optionsInputHeading,
          children: "Make Shareable"
        }), /*#__PURE__*/_jsx("span", {
          className: clsx(controlStyles.optionsInputSubHeading, controlStyles['optionsInputSubHeading--expanded']),
          children: "Allow users to create a share link to their answers on the page the widget is published on. Users that follow this link will see the same answers and results on that page."
        })]
      }), /*#__PURE__*/_jsx(Label, {
        forInput: "form-share-hash-id",
        className: parentStyles.formField,
        text: "Element Identifier",
        caption: "HTML element ID that should be applied to share link. Visitors will be directed to that area of the page. # symbol is not required.",
        children: /*#__PURE__*/_jsx(Input, {
          id: "form-share-hash-id",
          placeholder: "Element identifier",
          defaultValue: config.shareHashId,
          hasError: isValidShareElementId === false,
          hasSuccess: isValidShareElementId === true && Boolean(config.shareHashId),
          pattern: "^[a-zA-Z][\\w:.-]*$",
          onChange: evt => {
            var _evt$target, _evt$target2;

            const {
              valid
            } = evt === null || evt === void 0 ? void 0 : (_evt$target = evt.target) === null || _evt$target === void 0 ? void 0 : _evt$target.validity;
            const value = `${(evt === null || evt === void 0 ? void 0 : (_evt$target2 = evt.target) === null || _evt$target2 === void 0 ? void 0 : _evt$target2.value) || ''}`.trim();
            if (typeof valid === 'boolean') setIsValidShareElementId(valid);
            valid && onChange('', {
              shareHashId: value
            });
          }
        })
      })]
    }), /*#__PURE__*/_jsxs(RevealedSection, {
      label: "Developers",
      children: [/*#__PURE__*/_jsx(Label, {
        forInput: "form-custom-config-id-selector",
        className: parentStyles.formField,
        text: "Local Config Identifier",
        caption: "ID Attribute used to lookup a local, on page, configuration for this form.",
        children: /*#__PURE__*/_jsx(Input, {
          id: "form-custom-config-id-selector",
          placeholder: "Element identifier",
          defaultValue: config.localConfigId,
          hasError: isValidLocalConfigId === false,
          hasSuccess: isValidLocalConfigId === true && Boolean(config.localConfigId),
          pattern: "^[A-Za-z]+[\\w|\\-|:|\\.]*$",
          onChange: evt => {
            var _evt$target3, _evt$target4;

            const {
              valid
            } = evt === null || evt === void 0 ? void 0 : (_evt$target3 = evt.target) === null || _evt$target3 === void 0 ? void 0 : _evt$target3.validity;
            const value = `${(evt === null || evt === void 0 ? void 0 : (_evt$target4 = evt.target) === null || _evt$target4 === void 0 ? void 0 : _evt$target4.value) || ''}`.trim();

            if (typeof valid === 'boolean') {
              setIsValidLocalConfigId(valid);
            }

            valid && onChange('', {
              localConfigId: value
            });
          }
        })
      }), /*#__PURE__*/_jsx("strong", {
        children: "Example Local Config:"
      }), /*#__PURE__*/_jsx("img", {
        src: "/img/docs/widget-edit/local-config-example.webp"
      }), /*#__PURE__*/_jsx("small", {
        children: "Local configs are used to provide dynamic configurations to widget forms. These can be used to provide settings from a database or another source to configure results."
      })]
    })]
  });
};

export default EditorFormQuestions;